import { FC, useEffect, useState } from 'react';
import useTable from 'components/Base/Table/hooks/useTable';
import { TElcMeetingActionItemsUI } from 'data/types/elcs.types';
import {
  ETableStoragePaths,
  TSelectedRows,
} from 'components/Base/Table/Table.types';
import { Table } from 'components/Base/Table';
import CardContent from 'components/Base/Layout/Content/CardContent';
import { useGetElcMeetingActionItemQuery } from 'redux/apiSlice/elcs.slice';
import { Button } from '@packages/utils';
import { Icon, EIconNames } from 'components/Base/Icon';
import { TActionItemsTabProps, TAddNewRow } from './ActionItemsTab.types';
import useGetData from './ActionItemsTab.data';
import { SActionItemTabWrapper } from './ActionItem.styles';
import AddActionItem from './AddActionItem';
import AddTacticFromActionItemModal from './AddTacticFromActionItemModal';

const ActionItemsTab: FC<TActionItemsTabProps> = ({ elcId, meetingId }) => {
  const [newRowData, setNewRowData] = useState<TAddNewRow>();
  const [selectedRows, setSelectedRows] = useState<
    TSelectedRows<TElcMeetingActionItemsUI>
  >({
    selectedRowsKeys: [],
    selectedRowsRecords: [],
  });
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { defaultColumns } = useGetData(setNewRowData, elcId);

  const { columns, handleTableChange } = useTable<TElcMeetingActionItemsUI>({
    storagePath: ETableStoragePaths.MEETING_ACTION_ITEMS,
    defaultColumns,
  });

  const handleAddToActionPlan = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    // Clear selections after closing modal
    setSelectedRows({
      selectedRowsKeys: [],
      selectedRowsRecords: [],
    });
  };

  const { data, isLoading, isFetching, isSuccess } =
    useGetElcMeetingActionItemQuery({
      meetingId,
      page: 0,
      pageSize: 2000,
    });

  const newRow: TElcMeetingActionItemsUI = {
    meetingId,
    id: '',
    title: '',
    dueDate: '',
    ownerId: '',
    status: false,
    isNew: true,
  };

  const [dataSource, setDataSource] = useState<TElcMeetingActionItemsUI[]>([]);

  useEffect(() => {
    if (isSuccess && !isFetching && !isLoading) {
      if (data?.content.length) {
        setDataSource(data.content);
      } else {
        setDataSource([newRow]);
      }
    }
  }, [data?.content]);

  const hanldeAddActionItem = () => {
    setDataSource([...dataSource, newRow]);
  };
  const removeNewRow = () => {
    setDataSource(dataSource.filter(item => !item.isNew));
    setNewRowData(undefined);
  };

  return (
    <CardContent tabContent data-cy="meeting_tasks_section">
      <SActionItemTabWrapper>
        <Table<TElcMeetingActionItemsUI>
          tableTitle="Tasks"
          columns={columns}
          dataSource={dataSource}
          rowClassName={record => record.isNew && 'new-row'}
          loading={isFetching}
          onChange={handleTableChange}
          rowKey={record => record.id}
          storagePath={ETableStoragePaths.MEETING_ACTION_ITEMS}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          getCheckboxProps={(record: TElcMeetingActionItemsUI) => ({
            disabled: !!record.actionPlanTaskId,
            'data-cy': 'meeting_task_checkbox',
          })}
          headerActions={
            <Button
              icon={<Icon icon={EIconNames.PLUS} />}
              color="primary"
              type="primary"
              onClick={handleAddToActionPlan}
              disabled={selectedRows.selectedRowsKeys.length === 0}
              data-cy="add_to_action_plan_button"
            >
              Add to Action Plan
            </Button>
          }
          locale={{
            emptyText: () => <></>,
          }}
          footer={() => (
            <AddActionItem
              isFinishedGetActionItems={isSuccess && !isFetching && !isLoading}
              tableIsEmpty={dataSource.every(item => item.isNew)}
              tableHasNewRow={dataSource.some(item => item.isNew)}
              onAddNewRow={hanldeAddActionItem}
              onRemoveNewRow={removeNewRow}
              newRowData={newRowData}
              meetingId={meetingId}
              elcId={elcId}
              dataSource={dataSource}
            />
          )}
        />
      </SActionItemTabWrapper>

      <AddTacticFromActionItemModal
        open={isModalOpen}
        onClose={handleCloseModal}
        selectedActionItems={selectedRows.selectedRowsRecords}
        elcId={elcId}
      />
    </CardContent>
  );
};

export default ActionItemsTab;
